import { PointerEvent, useEffect, useRef, useState } from 'react';
import { Button, useToast } from '../../../../components/ui';
import { cleanSignature, loadImage } from './signatureImage';

/** Pad that records strokes drawn with a mouse, finger or pen. */
function DrawPad({ onDone, onCancel }: { onDone: (dataUrl: string) => void; onCancel: () => void }) {
  const toast = useToast();
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    // Sharp lines on high-density screens.
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0F172A';
  }, []);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = e.currentTarget.getContext('2d');
    const p = point(e);
    ctx?.beginPath();
    ctx?.moveTo(p.x, p.y);
    ctx?.lineTo(p.x + 0.1, p.y + 0.1);
    ctx?.stroke();
    setEmpty(false);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = e.currentTarget.getContext('2d');
    const p = point(e);
    ctx?.lineTo(p.x, p.y);
    ctx?.stroke();
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    const c = ref.current;
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
  };
  const done = () => {
    const url = ref.current && cleanSignature(ref.current);
    if (!url) return toast.error('Draw your signature first');
    onDone(url);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={ref}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className="h-44 w-full max-w-xl touch-none rounded-lg border border-dashed border-navy-300 bg-white"
        aria-label="Signature pad. Draw your signature here."
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" leftIcon="check" onClick={done} disabled={empty}>Use this signature</Button>
        <Button size="sm" variant="outline" onClick={clear} disabled={empty}>Clear</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/**
 * Authorised signature printed on quotations and invoices.
 * value / onChange: PNG data URL ('' when there is none). Uploaded photos are cleaned: the paper
 * background becomes transparent and the empty border is trimmed.
 */
export function SignatureInput({ value, onChange }: { value: string; onChange: (dataUrl: string) => void }) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [drawing, setDrawing] = useState(false);

  const pick = async (file?: File) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error('Pick an image smaller than 8 MB');
    try {
      const url = cleanSignature(await loadImage(file));
      if (!url) return toast.error('No signature found in this image. Use dark ink on white paper.');
      onChange(url);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  if (drawing) {
    return (
      <DrawPad
        onCancel={() => setDrawing(false)}
        onDone={(url) => {
          onChange(url);
          setDrawing(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex h-28 max-w-xl items-center justify-center rounded-lg border border-line bg-white p-3">
        {value ? (
          <img src={value} alt="Saved signature" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-sm text-ink-400">No signature yet</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" leftIcon="download" onClick={() => fileRef.current?.click()}>
          {value ? 'Replace with image' : 'Upload image'}
        </Button>
        <Button size="sm" variant="outline" leftIcon="edit" onClick={() => setDrawing(true)}>Draw signature</Button>
        {value && <Button size="sm" variant="dangerText" onClick={() => onChange('')}>Remove</Button>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

export default SignatureInput;
